import React, { useEffect } from "react";
import {
  Chart,
  ChartArea,
  ChartTitle,
  ChartSeries,
  ChartSeriesItem,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartLegend,
  ChartPanes,
  ChartPane,
  ChartTooltip,
} from "@progress/kendo-react-charts";
import moment from "moment";
import "hammerjs";
import { IKendoFinancialDrawdownChartProps } from "./KendoFinancialDrawdownChart.types";

export const KendoFinancialDrawdownChart = ({
  controls,
  name,
  modifiers,
  currencyFormatter,
  thickness,
  valuesInPercent = false,
  nameHidden = false,
  height = 200,
  width,
  drawdownData,
  truncateDates = true,
  t = (d) => d,
  categoryFormatter,
  isPdfExport = false, // New prop to handle PDF export
  ...props
}: IKendoFinancialDrawdownChartProps) => {
  const fontFamily = "'Nunito', sans-serif";
  const fontSize = controls?.expanded ? "15px" : "12px";
  const labelFont = `${fontSize} ${fontFamily}`;

  // Define default colors using CSS variables
  const defaultColors = {
    indexPerformanceColor: "var(--illio-chart-primary-line-color, #3FAAA4)",
    watermarkColor: "var(--illio-chart-watermark-color, #EA7250)",
    drawdownColor: "var(--illio-chart-drawdown-color, #E5422B)",
    titleColor: "var(--illio-chart-title-text, #888)",
    axisColor: "var(--illio-chart-axis-color, #EEE)",
    axisTextColor: "var(--illio-chart-axis-text-color, #666)",
    topPanelBg: "transparent",
    bottomPanelBg: "transparent",
  };

  // PDF export colors
  const pdfColors = {
    indexPerformanceColor: "rgb(23, 19, 42)", // indexPerf color
    watermarkColor: "rgb(63, 170, 164)", // watermark color
    drawdownColor: "rgb(234, 114, 80)", // drawdown color
    titleColor: "rgb(136, 136, 136)", // title color
    axisColor: "rgb(136, 136, 136)", // axis color
    axisTextColor: "rgb(102, 102, 102)", // axis text color
    topPanelBg: "rgba(0, 0, 0, 0)", // transparent
    bottomPanelBg: "rgba(0, 0, 0, 0)", // transparent
  };

  // Use modifiers colors if provided, otherwise fall back to default or PDF colors
  const colors = isPdfExport
    ? pdfColors
    : {
        ...defaultColors,
        ...modifiers?.colors,
      };

  const categories = drawdownData?.map((d) => d?.date);

  const maxChartValue =
    Math.max(...(drawdownData ?? [])?.map((d) => d?.perfIndex ?? 160)) + 5;
  const minChartValue =
    Math.min(...(drawdownData ?? [])?.map((d) => d?.perfIndex ?? 95)) - 5;

  return (
    <>
      <Chart
        {...props}
        style={{ background: "transparent", width, height }}
        transitions={false}
      >
        <ChartArea background="transparent" height={height} width={width} />
        <ChartTooltip
          padding={20}
          opacity={0.9}
          background={isPdfExport ? "#333333" : "var(--dark)"}
          border={{
            width: 0,
            color: "transparent",
          }}
          render={(props) => {
            return (
              <>
                <div style={{ color: "white" }}>
                  {categoryFormatter?.(props?.categoryText)}
                </div>
                {props.points?.map((point) => {
                  if (point.dataItem.hasOwnProperty("perfIndex")) {
                    return (
                      <div style={{ color: "white" }}>
                        <span>
                          <svg width="20" height="15">
                            <circle
                              fill={colors?.indexPerformanceColor}
                              r={6}
                              cx={6}
                              cy={6}
                            />
                          </svg>
                        </span>
                        Portfolio: {point.value}
                      </div>
                    );
                  }
                  if (point.dataItem.hasOwnProperty("maxPerf")) {
                    return (
                      <div style={{ color: "white" }}>
                        <span>
                          <svg width="20" height="15">
                            <circle
                              fill="transparent"
                              stroke={colors?.watermarkColor}
                              strokeDasharray="2,2"
                              r={6}
                              cx={6}
                              cy={6}
                            />
                          </svg>
                        </span>
                        Watermark: {point.value}
                      </div>
                    );
                  }
                  if (point.dataItem.hasOwnProperty("drawdown")) {
                    return (
                      <div style={{ color: "white" }}>
                        <span>
                          <svg width="20" height="15">
                            <circle
                              fill={colors.drawdownColor}
                              r={6}
                              cx={6}
                              cy={6}
                            />
                          </svg>
                        </span>
                        Drawdown: {point.value}
                      </div>
                    );
                  }
                  return null;
                })}
              </>
            );
          }}
          shared={modifiers?.tooltip?.shared || true}
        />
        <ChartPanes>
          <ChartPane
            name="top"
            background={colors?.topPanelBg ?? "transparent"}
            height={height * 0.5}
          />
          <ChartPane
            name="drawdown"
            background={colors?.bottomPanelBg ?? "transparent"}
            height={height * 0.3}
          />
          <ChartPane name="bottom" height={height * 0.1} />
        </ChartPanes>
        <ChartLegend visible={false} position="top" />
        {!!name && !nameHidden && (
          <ChartTitle text={name} color={colors?.titleColor} />
        )}
        <ChartSeries>
          <ChartSeriesItem
            name={t("performance index")}
            type="line"
            markers={modifiers?.markers?.indexMarker ?? { visible: false }}
            width={thickness ?? modifiers?.lines?.indexLineThickness ?? 1}
            color={colors?.indexPerformanceColor}
            maxSize={1}
            field="perfIndex"
            categoryField="date"
            data={drawdownData}
          />
          <ChartSeriesItem
            name={t("watermark")}
            type="line"
            markers={modifiers?.markers?.watermarkMarker ?? { visible: false }}
            dashType="longDash"
            width={thickness ?? modifiers?.lines?.watermarkLineThickness ?? 1}
            color={colors?.watermarkColor}
            maxSize={0.2}
            field="maxPerf"
            categoryField="date"
            data={drawdownData}
          />
          <ChartSeriesItem
            type="bar"
            data={(drawdownData ?? [])?.map(() => 0)}
            axis="bottom"
          />
          <ChartSeriesItem
            type="area"
            missingValues="interpolate"
            name="drawdown"
            field="drawdown"
            opacity={1}
            color={colors?.drawdownColor}
            data={drawdownData}
            axis="drawdown"
          />
        </ChartSeries>
        <ChartValueAxis>
          <ChartValueAxisItem
            name="top"
            pane="top"
            max={maxChartValue}
            min={minChartValue}
            majorGridLines={{
              color: isPdfExport ? undefined : colors?.axisColor,
              width: 0.4,
            }}
          />
          <ChartValueAxisItem
            visible={true}
            name="drawdown"
            pane="drawdown"
            color={colors?.axisTextColor}
            majorGridLines={{
              color: isPdfExport ? undefined : colors?.axisColor,
              width: 0.4,
            }}
          />
          <ChartValueAxisItem
            name="bottom"
            pane="bottom"
            color="transparent"
            majorGridLines={{
              color: "transparent",
            }}
          />
        </ChartValueAxis>
        <ChartCategoryAxis>
          <ChartCategoryAxisItem
            type="date"
            baseUnit="days"
            pane="top"
            color="transparent"
            maxDivisions={10}
            categories={categories}
          />
          <ChartCategoryAxisItem
            type="date"
            baseUnit="days"
            pane="bottom"
            color={colors?.axisTextColor}
            maxDivisions={10}
            labels={{
              color: colors?.axisTextColor,
              rotation: "auto",
              content: (e: any) => moment(e.value).format(`MMM 'YY`),
            }}
            categories={categories}
          />
        </ChartCategoryAxis>
      </Chart>
    </>
  );
};
